// frontend/src/components/AlignedTextPanel.js - Refactored
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AlignedTextPanel.css';

/**
 * AlignedTextPanel - A refactored component that separates text editing from alignment visualization
 * 
 * This component handles:
 * 1. Text display and editing
 * 2. Synchronized scrolling
 * 3. Applying multiple independent post-processing effects (alignments) to displayed text
 */
const AlignedTextPanel = ({ 
  text, 
  isSource = false, 
  isEditable = false,
  onTextChange = null,
  onScroll = null,
  className = '',
  activeWordData = null,
  highlightDirection = 'none',
  alignments = [],  // This will now be an array of alignment objects from different processors
  onWordClick = null, // New prop for handling word clicks
  onWordHover = null
}) => {
  const [paragraphs, setParagraphs] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editableText, setEditableText] = useState(text || '');
  const [wordMetadata, setWordMetadata] = useState({}); // Store metadata about words including all alignments
  
  // Container and content refs
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const textareaRef = useRef(null);
  
  // Process text into paragraphs
  useEffect(() => {
    if (!text) {
      setParagraphs([]);
      setEditableText('');
      return;
    }
    
    setEditableText(text);
    
    // Split text into paragraphs
    const paraArray = text.split(/\n\s*\n/);
    setParagraphs(paraArray);
    
    // Reset editing mode when text changes externally
    setIsEditing(false);
  }, [text]);
  
  // Critical: Register scrollable elements with parent when they mount or change
  useEffect(() => {
    // Only proceed if we have valid elements and the onScroll handler
    if (!onScroll) return;
    
    // Get the currently active scrollable element
    const activeElement = isEditing ? textareaRef.current : contentRef.current;
    
    if (activeElement) {
      // Register the element with parent component
      onScroll({
        type: 'registerRef',
        element: activeElement,
        isSource: isSource,
        isEditing: isEditing
      });
    }
  }, [isEditing, isSource, onScroll, text]); // Add text as dependency to re-register when content changes
  
  // Process all alignments into a unified wordMetadata object
  useEffect(() => {
    // Skip if no alignments or paragraphs
    if (!alignments || !paragraphs || paragraphs.length === 0) {
      setWordMetadata({});
      return;
    }
    
    // Start with an empty metadata object
    const newMetadata = {};
    
    // Calculate global word indices for each paragraph
    let globalWordIndex = 0;
    const paragraphWordMap = paragraphs.map(paragraph => {
      const words = paragraph.split(/\s+/).filter(Boolean);
      const startIndex = globalWordIndex;
      globalWordIndex += words.length;
      return {
        startIndex,
        endIndex: globalWordIndex - 1,
        length: words.length
      };
    });
    
    // Process each alignment and add its data to the metadata
    alignments.forEach(alignmentData => {
      // Skip if no alignment data or it's not applicable to this panel
      if (!alignmentData || 
         (isSource && !alignmentData.src2tgt) || 
         (!isSource && !alignmentData.tgt2src)) {
        return;
      }
      
      const linkData = isSource ? alignmentData.src2tgt : alignmentData.tgt2src;
      
      // Process each word's link data
      if (Array.isArray(linkData)) {
        linkData.forEach((links, wordIndex) => {
          if (!newMetadata[wordIndex]) {
            newMetadata[wordIndex] = {
              alignments: [],
              highlights: [],
              isActive: false,
              isPrimary: false
            };
          }
          
          // Store alignment data
          newMetadata[wordIndex].alignments.push({
            type: alignmentData.type || 'word',
            links: links,
            probability: links.length > 0 ? links[0].p : 0
          });
        });
      }
    });
    
    // Apply any active highlights based on activeWordData
    if (activeWordData && highlightDirection !== 'none') {
      // Determine which direction we're highlighting
      const isHighlightingFromThis = 
        (isSource && highlightDirection === 'source-to-target') ||
        (!isSource && highlightDirection === 'target-to-source');
        
      const isHighlightingToThis = 
        (isSource && highlightDirection === 'target-to-source') ||
        (!isSource && highlightDirection === 'source-to-target');
      
      // If we're the source of highlighting, mark the active word
      if (isHighlightingFromThis && activeWordData.index !== undefined) {
        if (newMetadata[activeWordData.index]) {
          newMetadata[activeWordData.index].isActive = true;
        }
      }
      
      // If we're the target of highlighting, highlight linked words
      if (isHighlightingToThis && activeWordData.index !== undefined) {
        const sourceIndex = activeWordData.index;
        
        // Find the appropriate alignment data
        alignments.forEach(alignmentData => {
          if (!alignmentData) return;
          
          const linkData = isSource ? 
            alignmentData.tgt2src : // We're the source but highlighting from target
            alignmentData.src2tgt;  // We're the target but highlighting from source
          
          // Make sure we have data for this index
          if (Array.isArray(linkData) && sourceIndex < linkData.length) {
            const links = linkData[sourceIndex] || [];
            
            // Mark words as highlighted
            links.forEach(link => {
              const targetIndex = isSource ? link.j : link.i;
              
              if (!newMetadata[targetIndex]) {
                newMetadata[targetIndex] = {
                  alignments: [],
                  highlights: [],
                  isActive: false,
                  isPrimary: false
                };
              }
              
              // Add highlight data
              newMetadata[targetIndex].highlights.push({
                type: alignmentData.type || 'word',
                probability: link.p || 0,
                sourceIndex: sourceIndex
              });
              
              // Mark as primary if it's the first/best match
              if (links.indexOf(link) === 0) {
                newMetadata[targetIndex].isPrimary = true;
              }
            });
          }
        });
      }
    }
    
    setWordMetadata(newMetadata);
  }, [alignments, activeWordData, highlightDirection, isSource, paragraphs]);
  
  // Scroll handler
  const handleScroll = useCallback((e) => {
    if (!onScroll) return;
    
    // Get the actively scrolling element
    const scrollingElement = e.target;
    
    onScroll({
      type: 'scroll',
      element: scrollingElement,
      scrollTop: scrollingElement.scrollTop,
      scrollHeight: scrollingElement.scrollHeight,
      clientHeight: scrollingElement.clientHeight,
      isSource: isSource
    });
  }, [isSource, onScroll]);
  
  // Word hover handler
  const handleWordHover = useCallback((index, paraIndex, wordInParaIndex) => {
    if (isEditing || !onWordHover) return;
    
    onWordHover({
      index,
      paraIndex,
      wordInParaIndex,
      isSource
    });
  }, [isEditing, isSource, onWordHover]);
  
  // Word click handler
  const handleWordClick = useCallback((index, paraIndex, wordInParaIndex, event) => {
    // Prevent event propagation
    event.stopPropagation();
    
    if (isEditing || !onWordClick) return;
    
    onWordClick({
      index,
      paraIndex,
      wordInParaIndex,
      isSource
    });
  }, [isEditing, isSource, onWordClick]);
  
  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    if (isEditing || !onWordHover) return;
    onWordHover(null);
  }, [isEditing, onWordHover]);
  
  // Handle container click (start editing)
  const handleClick = useCallback((e) => {
    if (!isEditable || isEditing) return;
    
    // Get the click position relative to the content div
    const contentDiv = contentRef.current;
    if (!contentDiv) return;
    
    // Save current scroll position
    const currentScrollTop = contentDiv.scrollTop;
    
    // Calculate character position from click
    let clickedCharIndex = 0;
    
    // If we clicked on a word span, calculate position
    if (e.target.classList.contains('word')) {
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      
      if (range) {
        // Walk through all text nodes to calculate absolute character position
        const walker = document.createTreeWalker(
          contentDiv,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );
        
        let currentNode;
        let totalLength = 0;
        
        while ((currentNode = walker.nextNode())) {
          if (currentNode === range.startContainer) {
            clickedCharIndex = totalLength + range.startOffset;
            break;
          }
          totalLength += currentNode.textContent.length;
        }
      }
    } else {
      // If clicked elsewhere, try to find the nearest position
      const range = document.caretRangeFromPoint(e.clientX, e.clientY);
      
      if (range) {
        // Calculate total character offset
        const walker = document.createTreeWalker(
          contentDiv,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );
        
        let currentNode;
        let totalLength = 0;
        
        while ((currentNode = walker.nextNode())) {
          if (currentNode === range.startContainer) {
            clickedCharIndex = totalLength + range.startOffset;
            break;
          }
          totalLength += currentNode.textContent.length;
        }
      }
    }
    
    setIsEditing(true);
    
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        
        // Set cursor position
        textareaRef.current.setSelectionRange(clickedCharIndex, clickedCharIndex);
        
        // Restore scroll position
        textareaRef.current.scrollTop = currentScrollTop;
        
        // Register the new scrollable element
        if (onScroll) {
          onScroll({
            type: 'registerRef',
            element: textareaRef.current,
            isSource: isSource,
            isEditing: true
          });
        }
      }
    }, 0);
  }, [isEditable, isEditing, isSource, onScroll]);
  
  // Handle blur (finish editing)
  const handleBlur = useCallback(() => {
    if (!isEditing) return;
    
    // Save textarea scroll position before switching
    const currentScrollTop = textareaRef.current ? textareaRef.current.scrollTop : 0;
    
    setIsEditing(false);
    
    // Register content element again and restore scroll position
    setTimeout(() => {
      if (contentRef.current) {
        // Restore scroll position to div
        contentRef.current.scrollTop = currentScrollTop;
        
        if (onScroll) {
          onScroll({
            type: 'registerRef',
            element: contentRef.current,
            isSource: isSource,
            isEditing: false
          });
        }
      }
    }, 0);
    
    // Notify parent of text change if needed
    if (onTextChange && editableText !== text) {
      onTextChange(editableText);
    }
  }, [isEditing, isSource, onScroll, onTextChange, editableText, text]);
  
  // Handle textarea change
  const handleTextChange = useCallback((e) => {
    setEditableText(e.target.value);
  }, []);
  
  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      // Save scroll position before switching
      const currentScrollTop = textareaRef.current ? textareaRef.current.scrollTop : 0;
      
      setEditableText(text);
      setIsEditing(false);
      
      // Register content element again and restore scroll
      setTimeout(() => {
        if (contentRef.current) {
          // Restore scroll position
          contentRef.current.scrollTop = currentScrollTop;
          
          if (onScroll) {
            onScroll({
              type: 'registerRef',
              element: contentRef.current,
              isSource: isSource,
              isEditing: false
            });
          }
        }
      }, 0);
      
      e.preventDefault();
    } else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      // Save text and exit editing
      const currentScrollTop = textareaRef.current ? textareaRef.current.scrollTop : 0;
      
      setIsEditing(false);
      
      // Register content element again and restore scroll
      setTimeout(() => {
        if (contentRef.current) {
          // Restore scroll position
          contentRef.current.scrollTop = currentScrollTop;
          
          if (onScroll) {
            onScroll({
              type: 'registerRef',
              element: contentRef.current,
              isSource: isSource,
              isEditing: false
            });
          }
        }
      }, 0);
      
      // Notify parent if text changed
      if (onTextChange && editableText !== text) {
        onTextChange(editableText);
      }
      
      e.preventDefault();
    }
  }, [editableText, isSource, onScroll, onTextChange, text]);
  
  // Determine if a word has any highlights
  const getWordStylingInfo = useCallback((wordGlobalIndex) => {
    const metadata = wordMetadata[wordGlobalIndex];
    if (!metadata) {
        return { isHighlighted: false, intensity: 0, isPrimary: false, isActive: false, isGlossaryMatch: false, glossaryHighlightStyle: 'normal' };
    }
    
    const isActive = metadata.isActive;
    const isPrimary = metadata.isPrimary;
    
    const highlights = metadata.highlights || [];
    const isHighlighted = highlights.length > 0;
    const intensity = highlights.reduce((max, h) => Math.max(max, h.probability || 0), 0);
    
    let isGlossaryMatch = false;
    let glossaryHighlightStyle = 'normal'; // Default to 'normal'

    if (metadata.alignments) {
        for (const alignInfo of metadata.alignments) { // alignInfo is { type: 'glossary', links: [link1, link2], ... }
            if (alignInfo.type === 'glossary' && alignInfo.links && alignInfo.links.length > 0) {
                isGlossaryMatch = true; 
                
                // Check all links associated with this word for an 'error' style.
                // An 'error' style on any link for this word should make the word appear as an error.
                let foundErrorStyleInThisAlignInfo = false;
                for (const link of alignInfo.links) { // link is e.g. { ..., highlight_style: 'error', ... }
                    if (link.highlight_style === 'error') {
                        glossaryHighlightStyle = 'error';
                        foundErrorStyleInThisAlignInfo = true;
                        break; // Found an error style for this alignInfo, no need to check other links in *this* alignInfo
                    }
                }
                if (foundErrorStyleInThisAlignInfo) {
                    // If an error style was found from this specific alignment source (e.g., one glossary term match),
                    // it takes precedence for the word overall. So, we can break from the outer loop too.
                    break; 
                }
                // If no error style was found in this alignInfo.links, glossaryHighlightStyle remains its current value.
                // It will be 'normal' unless a previous alignInfo (if multiple glossary terms match the same word)
                // already set it to 'error'.
            }
        }
    }

    return { isHighlighted, intensity, isPrimary, isActive, isGlossaryMatch, glossaryHighlightStyle };
  }, [wordMetadata]);
  
  return (
    <div 
      className={`aligned-text-panel ${className}`}
      ref={containerRef}
      onClick={isEditable ? handleClick : undefined}
      onMouseLeave={handleMouseLeave}
    >
      {isEditing ? (
        <textarea
          ref={textareaRef}
          className="aligned-text-editor"
          value={editableText}
          onChange={handleTextChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onScroll={handleScroll}
        />
      ) : (
        <div 
          className="aligned-text-content"
          ref={contentRef}
          onScroll={handleScroll}
        >
          {paragraphs.map((paragraph, paraIndex) => {
            // Split the paragraph text into word tokens with whitespace preserved
            const paraWords = paragraph.split(/(\s+)/);
            
            // Calculate global index offset for this paragraph
            let globalIndexOffset = 0;
            for (let i = 0; i < paraIndex; i++) {
              globalIndexOffset += paragraphs[i].split(/\s+/).filter(w => w.trim().length > 0).length;
            }
            
            return (
              <div key={`paragraph-${paraIndex}`} className="paragraph">
                {paraWords.map((token, tokenIndex) => {
                  // For whitespace, just render it directly
                  if (token.trim() === '') {
                    return <span key={`space-${paraIndex}-${tokenIndex}`}>{token}</span>;
                  }
                  
                  // Calculate indices for this word
                  const wordInParaIndex = Math.floor(tokenIndex / 2);
                  const globalIndex = globalIndexOffset + wordInParaIndex;
                  
                  // Get highlight info for this word
                  const { isHighlighted, intensity, isPrimary, isActive, isGlossaryMatch, glossaryHighlightStyle } = getWordStylingInfo(globalIndex);

                  // Set classes based on highlight state
                  const classes = ['word'];
                  if (isPrimary && !isGlossaryMatch) classes.push('primary-match'); // Word-to-word primary
                  if (isActive) classes.push('active-word');

                  if (isGlossaryMatch) {
                      if (glossaryHighlightStyle === 'error') {
                          classes.push('glossary-match-error'); // New class for red glossary highlight
                      } else {
                          // If it's also a primary-match from word-to-word, make it combined
                          if (isPrimary) {
                              classes.push('primary-match', 'glossary-match'); // Combined style
                          } else {
                              classes.push('glossary-match'); // Normal green glossary highlight
                          }
                      }
                  } else if (isPrimary) { // Ensure primary-match is added if not a glossary match
                       classes.push('primary-match');
                  }
                  
                  return (
                    <span
                      key={`word-${paraIndex}-${tokenIndex}`}
                      className={classes.join(' ')}
                      data-hit={isHighlighted ? "1" : "0"}
                      style={{ '--alpha': intensity }}
                      onMouseEnter={() => handleWordHover(globalIndex, paraIndex, wordInParaIndex)}
                      onClick={(e) => handleWordClick(globalIndex, paraIndex, wordInParaIndex, e)}
                    >
                      {token}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AlignedTextPanel;